import api from './axios';

export const proxiesAPI = {
  /**
   * Получить список доступных прокси
   * @param {Object} params - Параметры запроса
   * @param {string} params.status - Фильтр по статусу (online, offline, error)
   * @returns {Promise} Список прокси
   */
  getList: async (params = {}) => {
    const response = await api.get('/api/v1/proxies', { params });
    return response.data;
  },

  /**
   * Пинг прокси-сервера напрямую (из браузера)
   * Измеряет задержку между браузером пользователя и прокси-сервером
   * @param {Object} proxy - Объект прокси
   * @param {string} proxy.domain - Домен прокси
   * @param {string} proxy.ipAddress - IP адрес прокси
   * @param {number} proxy.pingPort - Порт HTTP ping сервера
   * @param {Object} options - Опции пинга
   * @param {number} options.count - Количество пингов (по умолчанию 5)
   * @param {Function} options.onProgress - Колбэк для обновления прогресса (current, total)
   * @returns {Promise<Object>} Результат пинга { success, latency, error, attempts }
   */
  pingProxy: async (proxy, options = {}) => {
    const { domain, ipAddress, pingPort } = proxy;
    const { count = 5, onProgress } = options;
    
    // Если pingPort не указан, возвращаем ошибку
    if (!pingPort) {
      return {
        success: false,
        latency: null,
        error: 'Ping port not configured',
        attempts: []
      };
    }

    const host = domain || ipAddress;
    if (!host) {
      return {
        success: false,
        latency: null,
        error: 'No host address available',
        attempts: []
      };
    }

    // Формируем URL для ping запроса
    const protocol = window.location.protocol; // Используем тот же протокол (http/https)
    const pingUrl = `${protocol}//${host}:${pingPort}/ping`;
    
    // Массив для хранения результатов всех попыток
    const attempts = [];
    const successfulLatencies = [];

    // Выполняем несколько пингов
    for (let i = 0; i < count; i++) {
      // Уведомляем о прогрессе
      if (onProgress) {
        onProgress(i + 1, count);
      }

      try {
        const startTime = performance.now();
        
        // Используем fetch с таймаутом
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1000); // 5 секунд таймаут
        
        const response = await fetch(pingUrl, {
          method: 'GET',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const endTime = performance.now();
        const latency = Math.round(endTime - startTime);
        
        attempts.push({
          attempt: i + 1,
          success: true,
          latency,
          error: null
        });
        
        successfulLatencies.push(latency);
        
      } catch (error) {
        attempts.push({
          attempt: i + 1,
          success: false,
          latency: null,
          error: error.name === 'AbortError' ? 'Timeout' : error.message
        });
      }

      // Небольшая задержка между пингами (100мс)
      if (i < count - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Если хотя бы один пинг был успешным
    if (successfulLatencies.length > 0) {
      const minLatency = Math.min(...successfulLatencies);
      const avgLatency = Math.round(successfulLatencies.reduce((a, b) => a + b, 0) / successfulLatencies.length);
      const maxLatency = Math.max(...successfulLatencies);
      
      return {
        success: true,
        latency: minLatency, // Возвращаем минимальную задержку
        avgLatency,
        maxLatency,
        host,
        port: pingPort,
        attempts,
        successfulCount: successfulLatencies.length,
        failedCount: count - successfulLatencies.length
      };
    }
    
    // Все пинги не удались
    return {
      success: false,
      latency: null,
      host,
      port: pingPort,
      error: 'All ping attempts failed',
      attempts,
      successfulCount: 0,
      failedCount: count
    };
  }
};

